import React, { useMemo, useState, useCallback, useEffect } from 'react'
import {
	Box,
	Alert,
	Checkbox,
	Collapse,
	Fade,
	FormControlLabel,
	IconButton,
	Menu,
	MenuItem,
	Tooltip,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material'
import TableRowsIcon from '@mui/icons-material/TableRows'
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { GridColDef, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-pro'
import { AnimatedDataGridPro, useDataGridAnimations, type DensityMode } from '../v2/general/CustomDataGrid'
import type { VirtualElement } from '@popperjs/core'
import { useNavigate } from 'react-router-dom'
import CustomButton from '../v2/general/CustomButton/CustomButton'
import CustomColumnMenu, { columnMenuPopperSx } from '../v2/general/CustomDataGrid/CustomColumnMenu'
import { useColumnOrder } from './useColumnOrder'
import scheduledServicesMock from '../../data/mockedData/schedules/scheduledServices.json'
import { useModal } from '../../store/ModalProvider'
import type { ServiceItineraryModalData } from '../../types/modal'

interface ScheduledService {
	id: number
	startDate: string
	startTime: string
	service: string
	startLocation: string
	endLocation: string
	endTime: string
	endDate: string
	client: string
	observations: string
	driver: string
	vehicle: string
}

interface ScheduledServicesProps {
	rows?: ScheduledService[]
	selectedRowIds?: GridRowSelectionModel
	onSelectionChange?: (selection: GridRowSelectionModel) => void
	compatibilityAlert?: string
	onCloseCompatibilityAlert?: () => void
}

const ScheduledServices = React.memo(function ScheduledServices({
	rows,
	selectedRowIds,
	onSelectionChange,
	compatibilityAlert,
	onCloseCompatibilityAlert,
}: ScheduledServicesProps) {
	const [density, setDensity] = useState<DensityMode>('standard')
	const [internalSelectionModel, setInternalSelectionModel] = useState<GridRowSelectionModel>([])
	const selectionModel = selectedRowIds ?? internalSelectionModel
	const applySelection = useCallback(
		(nextSelection: GridRowSelectionModel) => {
			if (onSelectionChange) {
				onSelectionChange(nextSelection)
				return
			}
			setInternalSelectionModel(nextSelection)
		},
		[onSelectionChange],
	)
	const [onlyWithoutDriver, setOnlyWithoutDriver] = useState(false)
	const [onlyWithoutVehicle, setOnlyWithoutVehicle] = useState(false)
	const [contextMenu, setContextMenu] = useState<{
		mouseX: number
		mouseY: number
		rowId: number | null
	} | null>(null)
	const [tooltipPosition, setTooltipPosition] = useState<{
		x: number
		y: number
	} | null>(null)
	const navigate = useNavigate()
	const { openModal } = useModal()

	// Use our custom animation hook
	const { animateRows } = useDataGridAnimations()

	const services = useMemo(() => (rows ?? scheduledServicesMock) as ScheduledService[], [rows])
	const selectedCount = selectionModel.length
	const hasSelection = selectedCount > 0

	const handleDensityChange = (_event: React.MouseEvent<HTMLElement>, newDensity: DensityMode | null) => {
		if (newDensity) {
			setDensity(newDensity)
		}
	}

	const handleOpenService = useCallback(
		(serviceId: number) => {
			navigate(`/schedules/service/${serviceId}`)
		},
		[navigate],
	)

	const handleUnschedule = () => {
		//console.log('Descalar servicos:', selectionModel);
	}

	const handleRemoveDriver = () => {
		console.log('Remover motorista:', selectionModel)
	}

	const handleRemoveVehicle = () => {
		console.log('Remover viatura:', selectionModel)
	}

	const filteredRows = useMemo(() => {
		return services.filter(service => {
			if (onlyWithoutDriver && service.driver?.trim()) {
				return false
			}
			if (onlyWithoutVehicle && service.vehicle?.trim()) {
				return false
			}
			return true
		})
	}, [services, onlyWithoutDriver, onlyWithoutVehicle])
	const visibleCount = filteredRows.length

	const rowById = useMemo(() => {
		return new Map(filteredRows.map(row => [row.id, row]))
	}, [filteredRows])

	const tooltipAnchor = useMemo<VirtualElement | null>(() => {
		if (!tooltipPosition) {
			return null
		}

		return {
			getBoundingClientRect: () => ({
				x: tooltipPosition.x,
				y: tooltipPosition.y,
				top: tooltipPosition.y,
				left: tooltipPosition.x,
				right: tooltipPosition.x,
				bottom: tooltipPosition.y,
				width: 0,
				height: 0,
				toJSON: () => {},
			}),
		}
	}, [tooltipPosition])

	const getRowIdFromCell = (cell: HTMLElement) => {
		const rowElement = cell.closest('.MuiDataGrid-row')
		const rowId = rowElement?.getAttribute('data-id')
		if (!rowId) {
			return null
		}
		const parsedId = Number(rowId)
		return Number.isNaN(parsedId) ? null : parsedId
	}

	const getRowHighlightClass = (row: ScheduledService) => {
		const driverMissing = !row.driver?.trim()
		const vehicleMissing = !row.vehicle?.trim()

		if (driverMissing && vehicleMissing) {
			return 'row--missing-both'
		}

		if (driverMissing) {
			return 'row--missing-driver'
		}

		if (vehicleMissing) {
			return 'row--missing-vehicle'
		}

		if (row.observations) {
			return 'row--note'
		}

		return ''
	}

	const allRowIds = useMemo(() => filteredRows.map(row => row.id), [filteredRows])
	useEffect(() => {
		if (selectionModel.length === 0) {
			return
		}
		const allowedIds = new Set(allRowIds)
		const nextSelection = selectionModel.filter(id => allowedIds.has(Number(id)))
		if (nextSelection.length !== selectionModel.length) {
			applySelection(nextSelection)
		}
	}, [allRowIds, applySelection, selectionModel])
	const isAllSelected = allRowIds.length > 0 && selectionModel.length === allRowIds.length
	const isIndeterminate = selectionModel.length > 0 && selectionModel.length < allRowIds.length

	const handleToggleAll = (checked: boolean) => {
		applySelection(checked ? allRowIds : [])
	}

	const handleToggleRow = (rowId: number) => {
		const nextSelection = selectionModel.includes(rowId)
			? selectionModel.filter(id => id !== rowId)
			: [...selectionModel, rowId]
		applySelection(nextSelection)
	}

	const getRowClickField = (event: React.MouseEvent<HTMLElement>) => {
		const target = event.target as HTMLElement | null
		return target?.closest('[data-field]')?.getAttribute('data-field')
	}

	const handleRowClick = (params: GridRowParams<ScheduledService>, event: React.MouseEvent<HTMLElement>) => {
		const field = getRowClickField(event)
		if (field === 'actions') {
			return
		}
		if (event.detail > 1) {
			return
		}
		handleToggleRow(Number(params.id))
	}

	const handleOpenContextMenu = (event: React.MouseEvent, rowId: number) => {
		event.preventDefault()
		setContextMenu(
			contextMenu === null
				? {
						mouseX: event.clientX - 2,
						mouseY: event.clientY - 4,
						rowId,
					}
				: null,
		)
	}

	const handleCloseContextMenu = () => {
		setContextMenu(null)
	}

	const buildItineraryModalData = (row?: ScheduledService): ServiceItineraryModalData => ({
		serviceId: row?.id ?? '0',
		serviceType: 'Agência de Viagens',
		requestCode: row?.service ?? '1115_PE2_PNE2',
		startDateTime: row?.startDate && row?.startTime ? `${row.startDate} ${row.startTime}` : undefined,
		endDateTime: row?.endDate && row?.endTime ? `${row.endDate} ${row.endTime}` : undefined,
		observations: row?.observations,
		client: {
			name: row?.client,
		},
		itinerary: {
			description: row ? `${row.startLocation ?? ''} - ${row.endLocation ?? ''}`.trim() : undefined,
		},
	})

	const handleContextAction = (action: string) => {
		if (action === 'Ver Itinerário do Serviço') {
			const rowId = contextMenu?.rowId
			const row = rowId !== null && rowId !== undefined ? rowById.get(rowId) : undefined
			openModal('Requisicao do Servico', buildItineraryModalData(row), [])
			handleCloseContextMenu()
			return
		}
		console.log(`${action}:`, contextMenu?.rowId)
		handleCloseContextMenu()
	}

	const handleRowContextMenu = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault()
		const rowId = Number((event.currentTarget as HTMLElement).dataset.id)
		if (Number.isNaN(rowId)) {
			return
		}
		handleOpenContextMenu(event, rowId)
	}

	const isTooltipBlockedField = (field?: string) => field === 'startDate' || field === 'actions'

	const handleCellMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
		const cell = event.currentTarget as HTMLElement
		const field = cell.dataset.field
		if (isTooltipBlockedField(field)) {
			setTooltipPosition(null)
			return
		}

		const rowId = getRowIdFromCell(cell)
		if (rowId === null) {
			return
		}

		const row = rowById.get(rowId)
		if (!row || !getRowHighlightClass(row)) {
			setTooltipPosition(null)
			return
		}

		setTooltipPosition({ x: event.clientX, y: event.clientY })
	}

	const handleCellMouseLeave = (event: React.MouseEvent<HTMLElement>) => {
		const cell = event.currentTarget as HTMLElement
		const relatedTarget = event.relatedTarget as HTMLElement | null
		if (relatedTarget) {
			const currentRow = cell.closest('.MuiDataGrid-row')
			const nextRow = relatedTarget.closest?.('.MuiDataGrid-row')
			if (currentRow && nextRow && currentRow.getAttribute('data-id') === nextRow.getAttribute('data-id')) {
				return
			}
		}
		setTooltipPosition(null)
	}

	const baseColumns = useMemo<GridColDef[]>(
		() => [
			{
				field: 'startDate',
				headerName: 'Data Início',
				width: 124,
				sortable: false,
				renderHeader: () => (
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Checkbox
							checked={isAllSelected}
							indeterminate={isIndeterminate}
							size='small'
							color='primary'
							onChange={event => handleToggleAll(event.target.checked)}
						/>
						<Typography
							sx={{
								fontFamily: 'Poppins',
								fontWeight: 600,
								fontSize: '12px',
								color: '#8695AF',
								letterSpacing: '0.17px',
								lineHeight: '24px',
							}}
						>
							Data Início
						</Typography>
					</Box>
				),
				renderCell: params => {
					const rowId = Number(params.id)
					const isChecked = selectionModel.includes(rowId)
					return (
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<Checkbox
								checked={isChecked}
								size='small'
								color='primary'
								onClick={event => event.stopPropagation()}
								onChange={() => handleToggleRow(rowId)}
							/>
							<Typography
								sx={{
									fontFamily: 'Poppins',
									fontSize: '12px',
									lineHeight: 1.3,
									color: '#092056',
								}}
							>
								{params.value}
							</Typography>
						</Box>
					)
				},
			},
			{ field: 'startTime', headerName: 'Hora Início', width: 71 },
			{ field: 'service', headerName: 'Serviço', width: 57 },
			{
				field: 'startLocation',
				headerName: 'Local Início',
				width: 130,
			},
			{
				field: 'endLocation',
				headerName: 'Local Término',
				width: 130,
			},
			{ field: 'endTime', headerName: 'Hora Término', width: 89 },
			{ field: 'endDate', headerName: 'Data Término', width: 89 },
			{ field: 'client', headerName: 'Cliente', width: 130 },
			{
				field: 'observations',
				headerName: 'Observações',
				width: 130,
				sortable: false,
			},
			{ field: 'driver', headerName: 'Motorista', width: 107 },
			{ field: 'vehicle', headerName: 'Viatura', width: 51 },
			{
				field: 'actions',
				headerName: '',
				sortable: false,
				filterable: false,
				width: 50,
				cellClassName: 'actions-cell',
				renderCell: params => (
					<Box
						className='row-actions'
						sx={{
							display: 'flex',
							justifyContent: 'flex-end',
							width: '100%',
						}}
					>
						<IconButton
							size='small'
							onClick={event => {
								event.stopPropagation()
								handleOpenService(Number(params.id))
							}}
						>
							<OpenInNewIcon sx={{ fontSize: 18, color: '#00A5FF' }} />
						</IconButton>
					</Box>
				),
			},
		],
		[handleOpenService, handleToggleAll, handleToggleRow, isAllSelected, isIndeterminate, selectionModel],
	)
	const { orderedColumns, handleColumnOrderChange } = useColumnOrder(baseColumns)
	const cellPadding = density === 'compact' ? '2px 4px' : '4px 4px'

	return (
		<Box
			sx={{
				backgroundColor: 'background.paper',
				borderRadius: '10px',
				padding: '24px 16px',
				display: 'flex',
				flexDirection: 'column',
				height: hasSelection ? '547px' : '515px',
				overflow: 'hidden',
				flexShrink: 0,
			}}
		>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					paddingBottom: '16px',
					borderBottom: 'none',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '8px',
						alignItems: 'flex-start',
					}}
				>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
						<Typography
							sx={{
								fontFamily: 'Poppins',
								fontWeight: 600,
								fontSize: '24px',
								lineHeight: 1.334,
								color: '#092056',
							}}
						>
							{visibleCount} {visibleCount === 1 ? 'Escalado' : 'Escalados'}
						</Typography>
						<Fade
							in={hasSelection}
							timeout={200}
							mountOnEnter
							unmountOnExit
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: '16px',
								}}
							>
								<Box
									sx={{
										width: '1px',
										height: '24px',
										backgroundColor: '#8695AF',
									}}
								/>
								<Typography
									sx={{
										fontFamily: 'Poppins',
										fontWeight: 500,
										fontSize: '13px',
										lineHeight: 1.3,
										color: '#8695AF',
									}}
								>
									{selectedCount} selecionado(s):
								</Typography>
							</Box>
						</Fade>
					</Box>
					<Collapse
						in={hasSelection}
						timeout={200}
						mountOnEnter
						unmountOnExit
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<CustomButton
								variant='text'
								onClick={handleUnschedule}
								sx={{
									fontFamily: 'Poppins',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									color: '#00A5FF',
									textTransform: 'none',
									padding: 0,
									minWidth: 'auto',
									'&:hover': {
										backgroundColor: 'transparent',
									},
								}}
							>
								Descalar
							</CustomButton>
							<Box
								sx={{
									width: '1px',
									height: '24px',
									backgroundColor: '#8695AF',
								}}
							/>
							<CustomButton
								variant='text'
								onClick={handleRemoveDriver}
								sx={{
									fontFamily: 'Poppins',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									color: '#00A5FF',
									textTransform: 'none',
									padding: 0,
									minWidth: 'auto',
									'&:hover': {
										backgroundColor: 'transparent',
									},
								}}
							>
								Remover Motorista
							</CustomButton>
							<Box
								sx={{
									width: '1px',
									height: '24px',
									backgroundColor: '#8695AF',
								}}
							/>
							<CustomButton
								variant='text'
								onClick={handleRemoveVehicle}
								sx={{
									fontFamily: 'Poppins',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '22px',
									color: '#00A5FF',
									textTransform: 'none',
									padding: 0,
									minWidth: 'auto',
									'&:hover': {
										backgroundColor: 'transparent',
									},
								}}
							>
								Remover Viatura
							</CustomButton>
						</Box>
					</Collapse>
				</Box>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
					<FormControlLabel
						control={
							<Checkbox
								size='small'
								checked={onlyWithoutDriver}
								onChange={event => setOnlyWithoutDriver(event.target.checked)}
							/>
						}
						label='So Sem Motorista'
						sx={{
							margin: 0,
							'& .MuiTypography-root': {
								fontFamily: 'Poppins',
								fontSize: '12px',
								fontWeight: 600,
								color: '#092056',
							},
						}}
					/>
					<FormControlLabel
						control={
							<Checkbox
								size='small'
								checked={onlyWithoutVehicle}
								onChange={event => setOnlyWithoutVehicle(event.target.checked)}
							/>
						}
						label='So Sem Viatura'
						sx={{
							margin: 0,
							'& .MuiTypography-root': {
								fontFamily: 'Poppins',
								fontSize: '12px',
								fontWeight: 600,
								color: '#092056',
							},
						}}
					/>
					<ToggleButtonGroup
						value={density}
						exclusive
						onChange={handleDensityChange}
						size='small'
						sx={{
							border: '1px solid rgba(134, 149, 175, 0.15)',
							borderRadius: '4px',
							'& .MuiToggleButton-root': {
								border: 'none',
								borderRadius: 0,
								padding: '4.8px',
								'&:not(:last-child)': {
									borderRight: '0.6px solid rgba(134, 149, 175, 0.15)',
								},
								'&.Mui-selected': {
									backgroundColor: '#f5f7fc',
									'&:hover': {
										backgroundColor: '#f5f7fc',
									},
								},
							},
						}}
					>
						<ToggleButton
							value='compact'
							aria-label='densidade compacta'
						>
							<TableRowsIcon
								sx={{
									width: '14.4px',
									height: '14.4px',
									color: density === 'compact' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
								}}
							/>
						</ToggleButton>
						<ToggleButton
							value='standard'
							aria-label='densidade espacada'
						>
							<ViewHeadlineIcon
								sx={{
									width: '14.4px',
									height: '14.4px',
									color: density === 'standard' ? '#00A5FF' : 'rgba(134, 149, 175, 1)',
								}}
							/>
						</ToggleButton>
					</ToggleButtonGroup>
				</Box>
			</Box>

			{compatibilityAlert && (
				<Box sx={{ paddingBottom: '12px' }}>
					<Alert
						severity='warning'
						onClose={onCloseCompatibilityAlert}
						sx={{
							fontFamily: 'var(--font-family)',
							fontSize: '14px',
							fontWeight: 500,
							lettrerSpacing: '0.15px',
							lineHeight: '150%',
							color: '#092056',
							backgroundColor: 'rgba(255, 193, 7, 0.2)',
							padding: '0 16px',
							height: '37px',
						}}
					>
						{compatibilityAlert}
					</Alert>
				</Box>
			)}

			<Box
				sx={{ flex: 1, minHeight: 0 }}
				data-area='teste'
			>
				<AnimatedDataGridPro
					rows={filteredRows}
					columns={orderedColumns}
					onColumnOrderChange={handleColumnOrderChange}
					density={density}
					animateRows={animateRows}
					autoHeight={false}
					columnHeaderHeight={40}
					disableRowSelectionOnClick
					onRowClick={handleRowClick}
					onRowDoubleClick={params => handleOpenService(Number(params.id))}
					slots={{ columnMenu: CustomColumnMenu }}
					slotProps={{
						row: {
							onContextMenu: handleRowContextMenu,
						},
						cell: {
							onMouseEnter: handleCellMouseEnter,
							onMouseLeave: handleCellMouseLeave,
						},
						basePopper: {
							sx: columnMenuPopperSx,
						},
					}}
					getRowClassName={params => getRowHighlightClass(params.row as ScheduledService)}
					sx={[
						{
							border: 'none',
							'--DataGrid-rowBorderColor': 'transparent',
							'& .MuiDataGrid-columnHeaders': {
								backgroundColor: '#FFFFFF',
								borderBottom: '1px solid rgba(134, 149, 175, 0.15)',
								borderTop: 'none',
								boxShadow: 'none',
							},
							'& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
								padding: cellPadding,
							},
							'& .MuiDataGrid-columnHeaderTitle': {
								fontFamily: 'Poppins',
								fontWeight: 600,
								fontSize: '12px',
								color: '#8695AF',
								letterSpacing: '0.17px',
								lineHeight: '24px',
								textTransform: 'none',
							},
							'& .MuiDataGrid-columnSeparator': {
								opacity: 0,
								color: 'rgba(134, 149, 175, 0.6)',
								transition: 'opacity 0.2s ease',
							},
							'& .MuiDataGrid-columnHeader:hover .MuiDataGrid-columnSeparator': {
								opacity: 1,
							},
							'& .MuiDataGrid-columnSeparator--resizable': {
								cursor: 'col-resize',
							},
							'& .MuiDataGrid-columnHeader': {
								borderRight: 'none',
								alignItems: 'center',
							},
							'& .MuiDataGrid-cell': {
								display: 'flex',
								alignItems: 'center',
								fontFamily: 'Poppins',
								fontSize: '12px',
								color: '#092056',
								lineHeight: 1.3,
								borderBottom: 'none',
								borderRight: 'none',
							},
							'& .MuiDataGrid-cell.actions-cell': {
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'flex-end',
							},
							'& .MuiDataGrid-row': {
								backgroundColor: '#f5f7fc',
								borderBottom: 'none',
							},
							'& .row-actions': {
								opacity: 0,
								pointerEvents: 'none',
								transition: 'opacity 0.15s ease',
							},
							'& .MuiDataGrid-row:hover .row-actions': {
								opacity: 1,
								pointerEvents: 'auto',
							},
							'& .row--note': {
								backgroundColor: 'rgba(0, 165, 255, 0.06)',
							},
							'& .row--missing-driver': {
								backgroundColor: 'rgba(255, 193, 7, 0.16)',
							},
							'& .row--missing-vehicle': {
								backgroundColor: 'rgba(255, 87, 34, 0.12)',
							},
							'& .row--missing-both': {
								backgroundColor: 'rgba(255, 111, 0, 0.14)',
							},
						},
					]}
				/>
			</Box>
			<Menu
				open={Boolean(contextMenu)}
				onClose={handleCloseContextMenu}
				anchorReference='anchorPosition'
				anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}
				PaperProps={{
					sx: {
						backgroundColor: '#FFFFFF',
						borderRadius: '4px',
						boxShadow:
							'0px 3px 14px 2px rgba(9, 32, 86, 0.12), 0px 8px 10px 1px rgba(9, 32, 86, 0.14), 0px 5px 5px -3px rgba(9, 32, 86, 0.2)',
					},
				}}
				MenuListProps={{
					sx: {
						padding: '8px 0',
					},
				}}
			>
				<MenuItem
					onClick={() => handleContextAction('Ver Itinerário do Serviço')}
					sx={{ padding: '6px 16px' }}
				>
					<Typography
						sx={{
							fontFamily: 'Poppins',
							fontWeight: 500,
							fontSize: '12px',
							lineHeight: 1.5,
							color: '#092056',
						}}
					>
						Ver Itinerário do Serviço
					</Typography>
				</MenuItem>
				<MenuItem
					onClick={() => handleContextAction('Abrir Requisição')}
					sx={{ padding: '6px 16px' }}
				>
					<Typography
						sx={{
							fontFamily: 'Poppins',
							fontWeight: 500,
							fontSize: '12px',
							lineHeight: 1.5,
							color: '#092056',
						}}
					>
						Abrir Requisição
					</Typography>
				</MenuItem>
				<MenuItem
					onClick={() => handleContextAction('Ver Histórico de Alterações')}
					sx={{ padding: '6px 16px' }}
				>
					<Typography
						sx={{
							fontFamily: 'Poppins',
							fontWeight: 500,
							fontSize: '12px',
							lineHeight: 1.5,
							color: '#092056',
						}}
					>
						Ver Histórico de Alterações
					</Typography>
				</MenuItem>
				<MenuItem
					onClick={() => handleContextAction('Copiar Troço')}
					sx={{ padding: '6px 16px' }}
				>
					<Typography
						sx={{
							fontFamily: 'Poppins',
							fontWeight: 500,
							fontSize: '12px',
							lineHeight: 1.5,
							color: '#092056',
						}}
					>
						Copiar Troço
					</Typography>
				</MenuItem>
				<MenuItem
					onClick={() => handleContextAction('Descalar Serviço - ?????')}
					sx={{ padding: '6px 16px' }}
				>
					<Typography
						sx={{
							fontFamily: 'Poppins',
							fontWeight: 500,
							fontSize: '12px',
							lineHeight: 1.5,
							color: '#092056',
						}}
					>
						Descalar Serviço - ?????
					</Typography>
				</MenuItem>
				<MenuItem
					onClick={() => handleContextAction('Forçar Info do Motorista (externo)')}
					sx={{ padding: '6px 16px' }}
				>
					<Typography
						sx={{
							fontFamily: 'Poppins',
							fontWeight: 500,
							fontSize: '12px',
							lineHeight: 1.5,
							color: '#092056',
						}}
					>
						Forçar Info do Motorista (externo)
					</Typography>
				</MenuItem>
			</Menu>
			<Tooltip
				open={Boolean(tooltipAnchor)}
				placement='bottom-start'
				title={
					<Typography
						sx={{
							fontFamily: 'Poppins',
							fontWeight: 500,
							fontSize: '10px',
							lineHeight: '14px',
							color: '#092056',
							whiteSpace: 'nowrap',
						}}
					>
						Confirme as Observações
					</Typography>
				}
				PopperProps={{
					anchorEl: tooltipAnchor,
				}}
				componentsProps={{
					tooltip: {
						sx: {
							backgroundColor: '#FFFFFF',
							borderRadius: '4px',
							pointerEvents: 'none',
							padding: '10px',
							boxShadow:
								'0px 1px 10px 0px rgba(9, 32, 86, 0.12), 0px 4px 5px 0px rgba(9, 32, 86, 0.14), 0px 2px 4px -1px rgba(9, 32, 86, 0.2)',
							maxWidth: 'none',
						},
					},
				}}
				disableFocusListener
				disableHoverListener
				disableTouchListener
			>
				<Box sx={{ width: 0, height: 0 }} />
			</Tooltip>
		</Box>
	)
})

export default ScheduledServices
